import { useEffect, useMemo } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, EmptyState, Icon, ProgressBar, Skeleton } from "@xinghai/ui";
import { studentApi } from "../../lib/api";
import { useAuth } from "../auth/useAuth";
import type { AssignmentItem } from "../../lib/api";
import { qk } from "../../lib/queries";
import { isOnboarded, markOnboarded, needsWarmup } from "./onboarding";
import { levelOf, nextUp, stageSummaries } from "./grouping";
import type { StageSummary } from "./grouping";
import { PendingUploads } from "./PendingUploads";
import { Illustration } from "../shared/Illustration";
import type { IllustrationName } from "../shared/Illustration";

/** Where an assignment leads: 听辨 has its own screen. */
export function assignmentHref(a: AssignmentItem): string {
  return a.task === "T7_perception" ? `/s/listen/${a.id}` : `/s/task/${a.id}`;
}

function StageRow({ stage }: { stage: StageSummary }) {
  const navigate = useNavigate();
  const target = stage.firstTodo;
  const detail =
    stage.kind === "task" && stage.task === "T2_diagnostic" && stage.levelCount
      ? `第 ${stage.currentLevel} 关 / 共 ${stage.levelCount} 关`
      : `${stage.done}/${stage.total}`;

  return (
    <button
      type="button"
      className={`xz-stage${stage.complete ? " is-done" : ""}`}
      disabled={!target}
      onClick={target ? () => void navigate(assignmentHref(target)) : undefined}
    >
      {stage.kind === "ai_test" && stage.level && stage.level <= 3 ? (
        <Illustration
          name={`level-${stage.level}` as IllustrationName}
          size="thumb"
          className="xz-stage__thumb"
        />
      ) : (
        <span className="xz-stage__icon" aria-hidden="true">
          <Icon id={stage.complete ? "ui-check-circle" : stage.icon} />
        </span>
      )}
      <span className="xz-stage__body">
        <span className="xz-stage__head">
          <span className="xz-stage__name">{stage.label}</span>
          <span className="gz-caption">{detail}</span>
        </span>
        <ProgressBar
          value={stage.total === 0 ? 0 : stage.done / stage.total}
          label={`${stage.label} 进度`}
          tone={stage.complete ? "success" : "info"}
          size="sm"
        />
        <span className="gz-caption">
          {stage.complete ? "已完成" : `约还要 ${stage.etaRemaining} 分钟`}
        </span>
      </span>
      {!stage.complete && (
        <span className="xz-stage__go" aria-hidden="true">
          <Icon id="ui-chevron-right" />
        </span>
      )}
    </button>
  );
}

/** docs/UX.md §1.2 — 首页 = 一个大按钮 + 本次会话的 6 个环节. */
function HomeSkeleton() {
  return (
    <div className="xz-container xz-stack" aria-busy="true">
      <Skeleton variant="rect" height={120} />
      <Skeleton variant="rect" height={72} />
      <Skeleton variant="rect" height={72} />
    </div>
  );
}

export function TasksPage() {
  const { me, isLoading } = useAuth();
  const q = useQuery({ queryKey: qk.assignments, queryFn: () => studentApi.assignments() });
  const items = useMemo(() => q.data?.assignments ?? [], [q.data]);
  const stages = useMemo(() => stageSummaries(items), [items]);
  const next = useMemo(() => nextUp(items), [items]);

  // Remember a server-side "already past 试录" on this device, and push a
  // local finish up so view-as on another browser will not open the trial.
  useEffect(() => {
    if (me?.warmup_required === false || me?.has_recordings) markOnboarded();
  }, [me?.warmup_required, me?.has_recordings]);
  useEffect(() => {
    if (!isOnboarded() || me?.warmup_required !== true || me.has_recordings) return;
    void studentApi.finishWarmup().catch(() => undefined);
  }, [me?.warmup_required, me?.has_recordings]);

  // Wait until /auth/me says whether this student already recorded. Redirecting
  // on the local flag alone drops view-as, and a second browser, into 试录.
  if (!isOnboarded()) {
    if (isLoading) return <HomeSkeleton />;
    if (needsWarmup(me?.warmup_required)) return <Navigate to="/s/warmup" replace />;
  }

  if (q.isPending) return <HomeSkeleton />;

  const total = items.length;
  const done = items.filter((a) => a.state !== "todo").length;
  const remaining = total - done;

  if (total === 0 && me?.has_recordings) return <Navigate to="/s/recordings" replace />;

  if (total === 0) {
    return (
      <div className="xz-container xz-stack">
        {/* docs/ROADMAP.md B2 — 待上传 still matters when there is nothing left to record. */}
        <PendingUploads />
        <EmptyState
          icon={
            <img
              className="xz-mascot"
              src="/design-system/assets/brand-library/mascot-front.png"
              alt=""
              aria-hidden="true"
              width={160}
              height={160}
            />
          }
          title="今天还没有任务"
          description="老师发布后会出现在这里。"
          action={
            <Link className="gz-button" to="/s/recordings">
              看看我的录音
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="xz-container xz-stack xz-home">
      {/* docs/ROADMAP.md B2 — 「待上传 n 条」 */}
      <PendingUploads />

      {me?.profile_incomplete && (
        <Card padding="md">
          <h2 className="gz-type-title">完善资料</h2>
          <p className="gz-muted">补上方言、训练情况和出生年份之后，任务会更适合你。</p>
          <Link className="gz-button" to="/s/me">
            去填写
          </Link>
        </Card>
      )}

      {q.data?.consent_required && (
        <p className="gz-notice">
          <Icon id="ui-info" size={20} />
          还差一步：签署三项授权之后才能开始录音。<Link to="/consent"> 去签署</Link>
        </p>
      )}

      <Card padding="lg" className="xz-home__hero">
        {next ? (
          <>
            <Link
              className="gz-button gz-button--primary xz-home__cta"
              to={assignmentHref(next.assignment)}
            >
              <Icon id="ui-microphone" size={24} /> 继续录音
            </Link>
            <p className="xz-home__next">
              下一条：{next.stage.label}
              {next.stage.kind === "task" &&
              next.stage.task === "T2_diagnostic" &&
              next.stage.levelCount
                ? ` · 第 ${levelOf(next.stage, next.assignment.id)} 关`
                : ""}
              {` · 还剩 ${remaining} 条`}
            </p>
          </>
        ) : (
          <>
            <Illustration name="done-check" size="md" />
            <p className="gz-type-heading">今天辛苦了</p>
            <p className="gz-muted">这一轮的录音都完成了，明天见。</p>
            <Link className="gz-button" to="/s/recordings">
              看看我的录音
            </Link>
          </>
        )}
      </Card>

      <section className="xz-home__stages" aria-label="本次会话的环节">
        {stages.map((s) => (
          <StageRow key={s.id} stage={s} />
        ))}
      </section>
    </div>
  );
}
